import { jsx } from 'react/jsx-runtime';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { isDefined } from '@fabric-app/utils/guards';
import { useRef, useState, useEffect } from 'react';
import { styled } from 'styled-components';
import { MAX_TITLE_LENGTH } from '../resource-detail/mutations/useMutationResourceTitleRename.js';
import { chainShortcutHandlers } from '../keyboardShortcuts/utils.js';
import { isHotkey } from 'is-hotkey';

const Title = styled.h1.withConfig({
  displayName: "TiptapTitle__Title",
  componentId: "sc-580ba7f0-0"
})([
  `position:relative;display:block;font-size:1.875em;line-height:1.3;font-weight:600;margin-block-start:0em;margin-block-end:0.3em;margin-inline-start:0px;margin-inline-end:0px;padding:0 var(--tiptap-padding-horizontal);padding-top:var(--tiptap-padding-vertical);max-width:1082px;margin-left:auto;margin-right:auto;width:100%;&:focus,&:focus-visible{outline:none;}&:empty:before,&[data-empty='true']:before{position:absolute;content:'Untitled note';color:`,
  `;display:block;}`
], cssVar["color-text-placeholder"]);
const TiptapTitle = ({ title, onTitleChange, editor }) => {
  const titleRef = useRef(null);
  const [initialTitle] = useState(title?.replace(/\n/g, ""));
  const { value: titleValue, onChange: onTitleInputChange } = useInputControls(title, {
    onChange(value) {
      onTitleChange?.(value.replace(/\n/g, ""));
    }
  });
  useEffect(() => {
    if (!isDefined(title)) return;
    const titleNode = titleRef.current;
    if (!titleNode) return;
    const selection = window.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection?.getRangeAt(0) : null;
    let startOffset, endOffset;
    if (range && titleNode.contains(range.startContainer)) {
      startOffset = range.startOffset;
      endOffset = range.endOffset;
    }
    if (title != null && titleNode.innerText !== title) {
      titleNode.innerText = title;
      if (startOffset !== void 0 && endOffset !== void 0 && titleNode.firstChild) {
        const newRange = document.createRange();
        newRange.setStart(titleNode.firstChild, startOffset);
        newRange.setEnd(titleNode.firstChild, endOffset);
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
    }
  }, [
    editor,
    title
  ]);
  const onTitleDataTransfer = (e, content) => {
    if (!editor) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const text = content.getData("text/plain");
    const titleWithText = titleValue + text;
    if (titleWithText.length < MAX_TITLE_LENGTH && !text.includes("\n") && text) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    editor.chain().focus().setTextSelection(0).run();
    const dom = editor.view.dom;
    const event = new ClipboardEvent("paste", {
      clipboardData: content,
      composed: true
    });
    dom.dispatchEvent(event);
  };
  const onPasteIntoTitle = (e) => {
    onTitleDataTransfer(e, e.clipboardData);
  };
  const onDropIntoTitle = (e) => {
    onTitleDataTransfer(e, e.dataTransfer);
  };
  const onTitleKeyDown = (e) => {
    return chainShortcutHandlers(e, [
      (e2) => {
        if (!isHotkey("enter", e2)) return false;
        e2.preventDefault();
        e2.stopPropagation();
        editor?.chain().focus().setTextSelection(0).run();
        return true;
      },
      (e2) => {
        if (!isHotkey("arrowdown", e2) && !isHotkey("tab", e2)) return false;
        e2.preventDefault();
        e2.stopPropagation();
        editor?.chain().focus().setTextSelection(0).run();
        return true;
      }
    ]);
  };
  return /* @__PURE__ */ jsx(Title, {
    ref: titleRef,
    contentEditable: true,
    suppressContentEditableWarning: true,
    onInput: (e) => {
      onTitleInputChange(e.currentTarget.innerText);
    },
    "data-empty": titleValue === "" && title === "",
    onPaste: onPasteIntoTitle,
    onDrop: onDropIntoTitle,
    onKeyDown: onTitleKeyDown,
    children: initialTitle
  });
};

export { TiptapTitle };
//# sourceMappingURL=TiptapTitle.js.map
